import { useState, useEffect, useMemo, useRef } from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import { useNavigate, useParams } from 'react-router-dom';
import { db, functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import {
  doc, getDoc, collection, getDocs,
  query, orderBy, updateDoc, serverTimestamp,
} from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { overAllowanceMachineIds } from '../../lib/machineAllowance';
import { hqGetModel } from '../../lib/hqApi';
import { uploadMachineDocument, deleteMachineDocument } from '../../lib/documentUpload';
import MachinePassportPanel from '../../components/MachinePassportPanel';
import MachineCarbon from '../../components/MachineCarbon';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import StatusPill from '../../components/ui/StatusPill';
import Button from '../../components/ui/Button';
import Avatar from '../../components/ui/Avatar';
import EmptyState from '../../components/ui/EmptyState';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import Modal from '../../components/ui/Modal';
import { toast } from '../../components/ui/Toast';
import { useTranslation } from '../../i18n/LanguageContext';
import {
  IconArrowLeft,
  IconTractor,
  IconClipboardList,
  IconTool,
  IconUsers,
  IconFileText,
  IconShare,
  IconAlertTriangle,


  IconLock,
  IconLockOpen,
  IconDownload,
  IconClock,
  IconBook,
  IconPlus,
  IconArrowRight,
} from '@tabler/icons-react';

const TABS = [
  { key: 'overview',   labelKey: 'machines.tab_overview',   icon: IconTractor      },
  { key: 'history',    labelKey: 'machines.tab_history',    icon: IconClipboardList },
  { key: 'documents',  labelKey: 'machines.tab_documents',  icon: IconFileText     },
  { key: 'operators',  labelKey: 'machines.tab_operators',  icon: IconUsers        },
  { key: 'sharing',    labelKey: 'machines.tab_sharing',    icon: IconShare        },
  { key: 'incidents',  labelKey: 'machines.tab_incidents',  icon: IconAlertTriangle },
];

function DocumentCard({ title, typeLabel, Icon, doc, type, orgId, machineId, userId, canEdit, onUploaded, onDeleted, isGeneric }) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const hasFile = !!doc;

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset so same file can be re-selected
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setError(t('machines.pdf_only'));
      return;
    }
    setError(null);
    setUploading(true);
    setProgress(0);
    try {
      const result = await uploadMachineDocument({
        file, orgId, machineId, type, userId,
        onProgress: setProgress,
        replaceDoc: hasFile ? doc : null,
      });
      if (onUploaded) onUploaded(result);
    } catch (err) {
      setError(err.message);
    }
    setUploading(false);
    setProgress(0);
  };

  const startUpload = () => fileInputRef.current?.click();

  const startReplace = () => {
    if (window.confirm(t('machines.replace_confirm').replace('{name}', title))) {
      startUpload();
    }
  };

  const startDelete = async () => {
    if (!window.confirm(t('machines.delete_doc_confirm'))) return;
    try {
      await deleteMachineDocument({ orgId, machineId, docRecord: doc });
      if (onDeleted) onDeleted(doc.id);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div style={{
      background: 'var(--color-bg-primary)',
      border: '0.5px solid var(--color-border-light)',
      borderRadius: 'var(--radius-lg)',
      padding: 16,
      minHeight: 180,
      display: 'flex',
      flexDirection: 'column',
      transition: 'border-color 0.15s',
      position: 'relative',
    }}>
      <input ref={fileInputRef} type="file" accept="application/pdf" onChange={handleFileSelect} style={{ display: 'none' }} />

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{
          width: 38, height: 38, borderRadius: 'var(--radius-md)',
          background: hasFile ? 'rgba(232,97,10,0.08)' : 'var(--color-bg-secondary)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon size={18} stroke={1.5} color={hasFile ? 'var(--color-brand)' : 'var(--color-text-tertiary)'} />
        </div>
        <span style={{
          fontSize: 9, padding: '2px 8px', borderRadius: 'var(--radius-full)',
          background: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)',
          textTransform: 'uppercase', letterSpacing: '0.04em',
        }}>{typeLabel}</span>
      </div>

      <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 12, flex: 1 }}>
        {hasFile ? (doc.uploadedAt?.toDate ? doc.uploadedAt.toDate().toLocaleDateString() : t('machines.uploaded')) : t('machines.no_file_yet')}
      </div>

      {uploading ? (
        <div>
          <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
            {t('machines.uploading')} {Math.round(progress)}%
          </div>
          <div style={{ height: 4, background: 'var(--color-bg-tertiary)', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              width: `${progress}%`,
              background: 'var(--color-brand)',
              borderRadius: 2,
              transition: 'width 0.2s ease',
            }} />
          </div>
        </div>
      ) : hasFile ? (
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <a href={doc.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: 'var(--color-brand)', textDecoration: 'none' }}>
            {t('machines.view_document')} →
          </a>
          {canEdit && !isGeneric && (
            <span onClick={startReplace} style={{ fontSize: 12, color: 'var(--color-text-tertiary)', cursor: 'pointer' }}>
              {t('common.replace')}
            </span>
          )}
          {canEdit && isGeneric && (
            <span onClick={startDelete} style={{ fontSize: 12, color: 'var(--color-danger)', cursor: 'pointer' }}>
              {t('common.delete')}
            </span>
          )}
        </div>
      ) : canEdit ? (
        <div onClick={startUpload} style={{ fontSize: 12, color: 'var(--color-text-tertiary)', cursor: 'pointer' }}>
          {t('common.upload')} →
        </div>
      ) : (
        <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>—</div>
      )}

      {error && (
        <div style={{ marginTop: 8, padding: '6px 8px', background: 'var(--color-danger-light)', color: 'var(--color-danger)', borderRadius: 'var(--radius-sm)', fontSize: 11 }}>
          {error}
        </div>
      )}
    </div>
  );
}

function AddPrivateDocumentCard({ orgId, machineId, userId, onUploaded }) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.type !== "application/pdf") { setError(t('machines.pdf_only')); return; }
    setError(null); setUploading(true); setProgress(0);
    try {
      await uploadMachineDocument({ file, orgId, machineId, type: "private", userId, onProgress: setProgress });
      if (onUploaded) onUploaded();
    } catch (err) { setError(err.message); }
    setUploading(false); setProgress(0);
  };

  return (
    <div
      onClick={() => !uploading && fileInputRef.current?.click()}
      style={{
        background: "var(--color-bg-primary)",
        border: "1px dashed var(--color-border-medium)",
        borderRadius: "var(--radius-lg)",
        padding: 20,
        minHeight: 180,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        cursor: uploading ? "default" : "pointer",
        color: "var(--color-text-tertiary)",
        transition: "all 0.15s",
      }}
    >
      <input ref={fileInputRef} type="file" accept="application/pdf" onChange={handleFileSelect} style={{ display: "none" }} />
      {uploading ? (
        <>
          <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>{t('machines.uploading')} {Math.round(progress)}%</div>
          <div style={{ height: 4, width: "80%", background: "var(--color-bg-tertiary)", borderRadius: 2, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${progress}%`, background: "var(--color-brand)", borderRadius: 2, transition: "width 0.2s ease" }} />
          </div>
        </>
      ) : (
        <>
          <IconPlus size={28} stroke={1.25} />
          <div style={{ fontSize: 12 }}>{t('machines.add_document')}</div>
        </>
      )}
      {error && (
        <div style={{ marginTop: 8, padding: "6px 8px", background: "var(--color-danger-light)", color: "var(--color-danger)", borderRadius: "var(--radius-sm)", fontSize: 11 }}>{error}</div>
      )}
    </div>
  );
}

export default function MachineProfile() {
  const { t, language } = useTranslation();
  const { machineId } = useParams();
  const [passportHighlight, setPassportHighlight] = useState(0);
  const navigate = useNavigate();
  const { orgId, userProfile, role, org } = useAuth();

  const [machine, setMachine] = useState(null);
  const [serviceRecords, setServiceRecords] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [canonicalModel, setCanonicalModel] = useState(null);
  const [checks, setChecks] = useState([]);
  // Held only to rank this machine by recency against the allowance.
  const [fleet, setFleet] = useState([]);

  // Unblock modal
  const [showUnblockModal, setShowUnblockModal] = useState(false);
  const [unblockReason, setUnblockReason] = useState('');
  const [unblocking, setUnblocking] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!orgId || !machineId) return;
      try {
        const [machineSnap, serviceSnap, incidentsSnap, docsSnap, fleetSnap] = await Promise.all([
          getDoc(doc(db, 'organisations', orgId, 'machines', machineId)),
          getDocs(query(
            collection(db, 'organisations', orgId, 'machines', machineId, 'serviceRecords'),
            orderBy('dateOfService', 'desc')
          )),
          getDocs(query(
            collection(db, 'organisations', orgId, 'incidents'),
            orderBy('detectedAt', 'desc')
          )),
          getDocs(collection(db, 'organisations', orgId, 'machines', machineId, 'documents')),
          getDocs(collection(db, 'organisations', orgId, 'machines')),
        ]);

        if (machineSnap.exists()) {
          setMachine({ id: machineSnap.id, ...machineSnap.data() });
        }

        setServiceRecords(serviceSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setIncidents(incidentsSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(i => i.machineId === machineId));
        setDocuments(docsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setFleet(fleetSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [machineId, orgId]);

  const refreshDocuments = async () => {
    if (!orgId || !machineId) return;
    try {
      const docsSnap = await getDocs(collection(db, "organisations", orgId, "machines", machineId, "documents"));
      setDocuments(docsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Refresh documents failed", err);
    }
  };

  // Fetch canonical model from HQ if approved canonical refs exist
  useEffect(() => {
    if (!machine?.canonicalMakeId || !machine?.canonicalModelId) return;
    if (machine.canonicalModelStatus !== 'approved') return;
    let cancelled = false;
    (async () => {
      try {
        const result = await hqGetModel(machine.canonicalMakeId, machine.canonicalModelId);
        if (!cancelled) setCanonicalModel(result?.model || null);
      } catch (err) {
        console.warn('Canonical model fetch failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, [machine?.canonicalMakeId, machine?.canonicalModelId, machine?.canonicalModelStatus]);

  // Release goes through the callable, which resolves the cause rather than
  // clearing the flag. Writing isBlocked directly would let a machine read as
  // safe while the defect that stopped it is still open.
  useEffect(() => {
    if (!orgId || !machineId) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(query(
          collection(db, 'organisations', orgId, 'machines', machineId, 'checkRecords'),
          orderBy('submittedAt', 'desc')
        ));
        if (!cancelled) setChecks(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.warn('Check records fetch failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId, machineId]);

  const handleUnblock = async () => {
    if (!unblockReason.trim()) return;
    setUnblocking(true);
    try {
      const release = httpsCallable(functions, 'releaseMachineBlock');
      const res = await release({
        orgId,
        machineId,
        narrative: unblockReason.trim(),
      });

      const snap = await getDoc(doc(db, 'organisations', orgId, 'machines', machineId));
      if (snap.exists()) setMachine({ id: snap.id, ...snap.data() });

      setShowUnblockModal(false);
      setUnblockReason('');
      toast.success(res?.data?.released ? t('machines.returned_to_service') : t('machines.not_blocked'));
    } catch (err) {
      console.error(err);
      // A defect-caused block is released by resolving the defect, so send
      // the manager there rather than leaving them stuck on this modal.
      const defectId = err?.details?.defectId;
      if (defectId) {
        setShowUnblockModal(false);
        toast.error(t('machines.resolve_defect_first'));
        navigate(`/defects/${defectId}`);
      } else {
        toast.error(err?.message || t('machines.return_failed'));
      }
    }
    setUnblocking(false);
  };

  const formatDate = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString(language, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString(language, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const costByMonth = useMemo(() => {
    const buckets = {};
    serviceRecords.forEach(r => {
      if (!r.dateOfService) return;
      const month = r.dateOfService.slice(0, 7);
      if (!buckets[month]) buckets[month] = { service: 0, repair: 0 };
      const partsTotal = (r.parts || []).reduce((s, p) => s + ((Number(p.cost) || 0) * (Number(p.quantity) || 0)), 0);
      const total = (Number(r.cost) || 0) + partsTotal;
      buckets[month][r.type === 'repair' ? 'repair' : 'service'] += total;
    });
    const months = Object.keys(buckets).sort();
    let cumulative = Number(machine?.purchasePrice) || 0;
    return months.map(m => {
      cumulative += buckets[m].service + buckets[m].repair;
      return { month: m, service: buckets[m].service, repair: buckets[m].repair, cumulative };
    });
  }, [serviceRecords, machine?.purchasePrice]);

    if (loading) return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <LoadingSkeleton rows={6} card />
      </div>
    </Layout>
  );

  if (!machine) return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <div onClick={() => navigate('/machines')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 16 }}>
          <IconArrowLeft size={15} stroke={1.5} /> {t('machines.title')}
        </div>
        <div style={{ fontSize: 16, color: 'var(--color-text-secondary)' }}>{t('machines.machine_not_found')}</div>
      </div>
    </Layout>
  );



  // Presentation only — the record is still fetched and writes still land.
  const dimmed = overAllowanceMachineIds(fleet, org).has(machineId);

    const serviceHoursProgress = machine.nextServiceHours && machine.lastServiceHours
    ? ((machine.currentHours - machine.lastServiceHours) / (machine.nextServiceHours - machine.lastServiceHours)) * 100
    : null;

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>

        {/* Back */}
        <div onClick={() => navigate('/machines')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 16 }}>
          <IconArrowLeft size={15} stroke={1.5} /> {t('machines.title')}
        </div>

        {dimmed && (
          <div style={{
            background: '#fff', border: '0.5px solid var(--color-border-light)',
            borderRadius: 'var(--radius-lg)', padding: '12px 16px', marginBottom: 16,
            fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.6,
          }}>
            {t('billing.over_limit_full')}
          </div>
        )}

        <div style={{ opacity: dimmed ? 0.45 : 1, filter: dimmed ? 'grayscale(1)' : 'none' }}>

        {/* Header */}
        <div style={{
          display: 'flex',
          gap: 20,
          padding: 20,
          background: 'var(--color-bg-primary)',
          border: '0.5px solid var(--color-border-light)',
          borderRadius: 'var(--radius-lg)',
          marginBottom: 20,
        }}>
          {/* Square hero image */}
          <div style={{
            width: 260,
            alignSelf: 'stretch',
            flexShrink: 0,
            borderRadius: 'var(--radius-md)',
            overflow: 'hidden',
            background: machine.isBlocked ? 'var(--color-danger-light)' : 'var(--color-bg-secondary)',
            border: machine.isBlocked ? '0.5px solid var(--color-danger)' : '0.5px solid var(--color-border-light)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {(canonicalModel?.heroImageUrl || machine.imageURL) ? (
              <img
                src={canonicalModel?.heroImageUrl || machine.imageURL}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: machine.isBlocked ? 0.4 : 1 }}
              />
            ) : machine.isBlocked ? (
              <IconLock size={48} color="var(--color-danger)" stroke={1.5} />
            ) : (
              <IconTractor size={48} color="var(--color-text-tertiary)" stroke={1} />
            )}
          </div>

          {/* Right column: title, status, metadata */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            {/* Top row: title + status + actions */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                  <h1 style={{ fontSize: 22, fontWeight: 500, margin: 0 }}>
                    {machine.makeLabel || machine.make || t('common.unknown')} {machine.modelLabel || machine.model || ''}
                  </h1>
                  <StatusPill status={machine.isBlocked ? 'blocked' : 'active'} size="lg" />
                </div>
                {machine.category && (
                  <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', textTransform: 'capitalize' }}>
                    {machine.category}{machine.subCategory ? ` · ${machine.subCategory}` : ''}
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                {machine.isBlocked && (role === 'org_admin' || role === 'manager') && (
                  <Button
                    variant="success-outline"
                    icon={<IconLockOpen size={14} stroke={1.5} />}
                    onClick={() => setShowUnblockModal(true)}
                  >
                    {t('machines.unblock')}
                  </Button>
                )}
                <Button
                  variant="secondary"
                  icon={<IconDownload size={14} stroke={1.5} />}
                  onClick={() => {
                    setActiveTab('sharing');
                    setPassportHighlight(h => h + 1);
                  }}
                >
                  {t('common.export_pdf')}
                </Button>
                {(role === 'org_admin' || role === 'manager') && (
                  <Button
                    variant="primary"
                    icon={<IconPlus size={14} stroke={1.5} />}
                    onClick={() => navigate(`/machines/${machineId}/service/new`)}
                  >
                    {t('service.add_record')}
                  </Button>
                )}
              </div>
            </div>

            {/* Metadata grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, marginTop: 'auto', paddingTop: 12 }}>
              {machine.registration && (
                <div>
                  <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 3 }}>{t('machines.registration')}</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, background: '#FAC775', color: '#412402', padding: '2px 8px', borderRadius: 3, display: 'inline-block' }}>
                    {machine.registration}
                  </div>
                </div>
              )}
              {machine.serialNumber && (
                <div>
                  <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 3 }}>{t('machines.serial_number')}</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{machine.serialNumber}</div>
                </div>
              )}
              {machine.year && (
                <div>
                  <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 3 }}>{t('machines.year')}</div>
                  <div style={{ fontSize: 13 }}>{machine.year}</div>
                </div>
              )}
              {machine.siteName && (
                <div>
                  <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 3 }}>{t('machines.col_site')}</div>
                  <div style={{ fontSize: 13 }}>{machine.siteName}</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Blocked banner */}
        {machine.isBlocked && (
          <div style={{ background: 'var(--color-danger-light)', border: '0.5px solid var(--color-danger)', borderRadius: 'var(--radius-lg)', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <IconAlertTriangle size={18} color="var(--color-danger)" stroke={1.5} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 400, color: 'var(--color-danger)' }}>{t('machines.blocked_banner')}</div>
              {machine.blockedReason && (
                <div style={{ fontSize: 12, color: 'var(--color-danger)', opacity: 0.8, marginTop: 2 }}>{machine.blockedReason}</div>
              )}
            </div>
          </div>
        )}

        {/* Quick stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 20 }}>
          {[
            { label: t('machines.current_hours'), value: machine.currentHours ? `${machine.currentHours.toLocaleString()} ${t('common.unit_hrs')}` : '—', colour: 'var(--color-text-primary)' },
            { label: t('machines.next_service'), value: machine.nextServiceHours ? `${machine.nextServiceHours.toLocaleString()} ${t('common.unit_hrs')}` : '—', colour: serviceHoursProgress > 80 ? 'var(--color-danger)' : serviceHoursProgress > 60 ? 'var(--color-warning)' : 'var(--color-success)' },
            { label: t('machines.service_records'), value: serviceRecords.length, colour: 'var(--color-text-primary)' },
            { label: t('machines.tab_incidents'), value: incidents.length, colour: incidents.length > 0 ? 'var(--color-danger)' : 'var(--color-text-primary)' },
          ].map(s => (
            <div key={s.label} style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '14px 16px' }}>
              <div style={{ fontSize: 24, fontWeight: 400, color: s.colour, marginBottom: 4 }}>{s.value}</div>
              <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Service progress bar */}
        {serviceHoursProgress !== null && (
          <div style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '14px 16px', marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{t('machines.service_progress')}</span>
              <span style={{ fontSize: 12, color: serviceHoursProgress > 80 ? 'var(--color-danger)' : 'var(--color-text-secondary)' }}>
                {machine.currentHours?.toLocaleString()} / {machine.nextServiceHours?.toLocaleString()} {t('common.unit_hrs')}
              </span>
            </div>
            <div style={{ height: 6, background: 'var(--color-bg-tertiary)', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: `${Math.min(serviceHoursProgress, 100)}%`,
                background: serviceHoursProgress > 80 ? 'var(--color-danger)' : serviceHoursProgress > 60 ? 'var(--color-warning)' : 'var(--color-success)',
                borderRadius: 3,
                transition: 'width 0.3s ease',
              }} />
            </div>
          </div>
        )}

        {/* Cost chart */}
        <div style={{
          background: 'var(--color-bg-primary)',
          border: '0.5px solid var(--color-border-light)',
          borderRadius: 'var(--radius-lg)',
          padding: '16px 20px',
          marginBottom: 20,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 500 }}>{t('machines.cost_over_time')}</div>
              <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>
                {t('machines.cost_over_time_sub')}
              </div>
            </div>
          </div>
          {costByMonth.length === 0 ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', fontSize: 12, color: 'var(--color-text-tertiary)' }}>
              {t('machines.cost_empty')}
            </div>
          ) : (
            <div style={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={costByMonth} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-light)" />
                  <XAxis dataKey="month" stroke="var(--color-text-tertiary)" fontSize={11} />
                  <YAxis stroke="var(--color-text-tertiary)" fontSize={11} tickFormatter={v => `£${v}`} />
                  <Tooltip
                    contentStyle={{
                      background: 'var(--color-bg-primary)',
                      border: '0.5px solid var(--color-border-light)',
                      borderRadius: 'var(--radius-md)',
                      fontSize: 12,
                    }}
                    formatter={v => `£${Number(v).toFixed(2)}`}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="service" stroke="#E8610A" strokeWidth={2} name={t('machines.series_service')} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="repair" stroke="#DC2626" strokeWidth={2} name={t('machines.series_repair')} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="cumulative" stroke="#6B7280" strokeWidth={2} strokeDasharray="4 4" name={t('machines.series_cumulative')} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${TABS.length}, 1fr)`, marginBottom: 20, borderBottom: '0.5px solid var(--color-border-light)' }}>
          {TABS.map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.key;
            return (
              <div
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: '10px 14px',
                  fontSize: 13,
                  cursor: 'pointer',
                  color: active ? 'var(--color-brand)' : 'var(--color-text-secondary)',
                  borderBottom: active ? '2px solid var(--color-brand)' : '2px solid transparent',
                  marginBottom: -1,
                  transition: 'all 0.15s',
                }}
              >
                <Icon size={14} stroke={1.5} />
                {t(tab.labelKey)}
              </div>
            );
          })}
        </div>

        {/* Tab content */}

        {/* Overview */}
        {activeTab === 'overview' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <MachineCarbon
                machineId={machineId}
                machine={machine}
                checks={checks}
                onSaved={patch => setMachine(prev => ({ ...prev, ...patch }))}
              />
            </div>
            <Card>
              <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>{t('machines.details_title')}</div>
              {[
                { label: t('machines.make'), value: machine.makeLabel },
                { label: t('machines.model'), value: machine.modelLabel },
                { label: t('machines.category'), value: machine.category, capitalise: true },
                { label: t('machines.year'), value: machine.year },
                { label: t('machines.serial_number'), value: machine.serialNumber, mono: true },
                { label: t('machines.registration'), value: machine.registration, mono: true },
                { label: t('machines.current_hours'), value: machine.currentHours ? `${machine.currentHours.toLocaleString()} ${t('common.unit_hrs')}` : '—' },
                { label: t('machines.col_site'), value: machine.siteName },
                { label: t('machines.assigned_to'), value: machine.isPool ? t('machines.pool_machine') : (machine.assignedOperatorName || t('machines.unassigned')) },
              ].map(row => (
                <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '0.5px solid var(--color-border-light)' }}>
                  <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{row.label}</span>
                  <span style={{ fontSize: 13, color: 'var(--color-text-primary)', fontFamily: row.mono ? 'var(--font-mono)' : 'inherit', textTransform: row.capitalise ? 'capitalize' : 'none' }}>
                    {row.value || '—'}
                  </span>
                </div>
              ))}
            </Card>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Card>
                <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>{t('machines.purchase_value')}</div>
                {[
                  { label: t('machines.purchase_date'), value: machine.purchaseDate ? formatDate({ toDate: () => new Date(machine.purchaseDate) }) : '—' },
                  { label: t('machines.purchase_price'), value: machine.purchasePrice ? `£${machine.purchasePrice.toLocaleString()}` : '—' },
                  { label: t('machines.depreciation_rate'), value: machine.depreciationRate ? `${machine.depreciationRate}% ${t('machines.per_year')}` : '—' },
                  { label: t('machines.estimated_life'), value: machine.estimatedLifeYears ? `${machine.estimatedLifeYears} ${t('common.unit_years')}` : '—' },
                ].map(row => (
                  <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '0.5px solid var(--color-border-light)' }}>
                    <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{row.label}</span>
                    <span style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>{row.value}</span>
                  </div>
                ))}
              </Card>

              <Card>
                <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>{t('machines.cost_tracking')}</div>
                {[
                  { label: t('machines.total_service_cost'), value: machine.costTracking?.totalServiceCost ? `£${machine.costTracking.totalServiceCost.toLocaleString()}` : '£0' },
                  { label: t('machines.total_repair_cost'), value: machine.costTracking?.totalRepairCost ? `£${machine.costTracking.totalRepairCost.toLocaleString()}` : '£0' },
                  { label: t('machines.total_fuel_cost'), value: machine.costTracking?.totalFuelCost ? `£${machine.costTracking.totalFuelCost.toLocaleString()}` : '£0' },
                ].map(row => (
                  <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '0.5px solid var(--color-border-light)' }}>
                    <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{row.label}</span>
                    <span style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>{row.value}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-primary)' }}>{t('machines.total_cost_ownership')}</span>
                  <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-primary)' }}>
                    £{(
                      (machine.costTracking?.totalServiceCost || 0) +
                      (machine.costTracking?.totalRepairCost || 0) +
                      (machine.costTracking?.totalFuelCost || 0) +
                      (machine.purchasePrice || 0)
                    ).toLocaleString()}
                  </span>
                </div>
              </Card>
            </div>
          </div>
        )}

        {/* History */}
        {activeTab === 'history' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 400 }}>{t('machines.service_history')}</div>
              <Button
                variant="primary"
                size="sm"
                icon={<IconPlus size={13} stroke={1.5} />}
                onClick={() => navigate(`/machines/${machineId}/service/new`)}
              >
                {t('service.add_record_short')}
              </Button>
            </div>

            {serviceRecords.length === 0 ? (
              <Card>
                <EmptyState
                  icon={<IconTool size={28} stroke={1.5} />}
                  title={t('service.none_yet')}
                  message={t('service.none_yet_msg')}
                />
              </Card>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {serviceRecords.map(record => (
                  <Card
                    key={record.id}
                    onClick={() => navigate(`/machines/${machineId}/service/${record.id}`)}
                    hover
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--color-text-primary)', textTransform: 'capitalize' }}>{record.type}</span>
                          {record.sealed && (
                            <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 20, background: '#E6F1FB', color: '#185FA5' }}>{t('service.sealed')}</span>
                          )}
                          {/* Pushed from a linked workshop. Sealed at source,
                              so it is a record here rather than an entry. */}
                          {record.source === 'workshop' && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, padding: '1px 6px', borderRadius: 20, background: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)' }}>
                              <IconLock size={9} stroke={2} />
                              {record.workshopName || t('service.workshop')}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 4 }}>
                          {record.description || '—'}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                            <IconClock size={11} stroke={1.5} style={{ verticalAlign: -1, marginRight: 3 }} />
                            {formatDate(record.dateOfService)}
                          </span>
                          {record.hoursAtService && (
                            <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                              {record.hoursAtService.toLocaleString()} {t('common.unit_hrs')}
                            </span>
                          )}
                          {record.performedBy && (
                            <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                              {record.performedBy}
                            </span>
                          )}
                          {record.workshopJobRef && (
                            <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)', fontFamily: 'var(--font-mono, monospace)' }}>
                              {record.workshopJobRef}
                            </span>
                          )}
                          {Array.isArray(record.media) && record.media.length > 0 && (
                            <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                              {record.media.length} {t(record.media.length === 1 ? 'common.photo_one' : 'common.photo_many')}
                            </span>
                          )}
                        </div>
                      </div>
                      {record.cost && (
                        <div style={{ fontSize: 14, color: 'var(--color-text-primary)', flexShrink: 0, marginLeft: 12 }}>
                          £{record.cost.toLocaleString()}
                        </div>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Documents */}
        {activeTab === 'documents' && (
          <div>
            <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)', marginBottom: 16, lineHeight: 1.6 }}>
              {t('machines.documents_intro')}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
              {/* Operator's manual slot */}
              <DocumentCard
                title={t('machines.doc_operators_manual')}
                typeLabel={t('machines.doc_type_manual')}
                Icon={IconBook}
                doc={documents.find(d => d.type === 'operators_manual')}
                type="operators_manual"
                orgId={orgId}
                machineId={machineId}
                userId={userProfile?.id || userProfile?.uid}
                canEdit={role === 'org_admin' || role === 'manager'}
                isGeneric={false}
                onUploaded={() => refreshDocuments()}
              />

              {/* Workshop manual slot */}
              <DocumentCard
                title={t('machines.doc_workshop_manual')}
                typeLabel={t('machines.doc_type_manual')}
                Icon={IconTool}
                doc={documents.find(d => d.type === 'workshop_manual')}
                type="workshop_manual"
                orgId={orgId}
                machineId={machineId}
                userId={userProfile?.id || userProfile?.uid}
                canEdit={role === 'org_admin' || role === 'manager'}
                isGeneric={false}
                onUploaded={() => refreshDocuments()}
              />

              {/* Private documents as individual cards */}
              {documents
                .filter(d => d.type !== 'operators_manual' && d.type !== 'workshop_manual')
                .map(d => (
                  <DocumentCard
                    key={d.id}
                    title={d.name || t('machines.untitled_document')}
                    typeLabel={t('machines.doc_type_private')}
                    Icon={IconFileText}
                    doc={d}
                    type="private"
                    orgId={orgId}
                    machineId={machineId}
                    userId={userProfile?.id || userProfile?.uid}
                    canEdit={role === 'org_admin' || role === 'manager'}
                    isGeneric={true}
                    onDeleted={() => refreshDocuments()}
                  />
                ))}

              {/* + Add document */}
              {(role === 'org_admin' || role === 'manager') && (
                <AddPrivateDocumentCard
                  orgId={orgId}
                  machineId={machineId}
                  userId={userProfile?.id || userProfile?.uid}
                  onUploaded={() => refreshDocuments()}
                />
              )}
            </div>
          </div>
        )}

        {/* Operators */}
        {activeTab === 'operators' && (
          <div>
            <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 16 }}>{t('machines.assigned_operators')}</div>
            {!machine.assignedOperatorId && !machine.isPool ? (
              <Card>
                <EmptyState
                  icon={<IconUsers size={28} stroke={1.5} />}
                  title={t('machines.no_operator')}
                  message={t('machines.no_operator_msg')}
                />
              </Card>
            ) : machine.isPool ? (
              <Card>
                <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', textAlign: 'center', padding: '16px 0' }}>
                  {t('machines.pool_machine_msg')}
                </div>
              </Card>
            ) : (
              <Card>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0' }}>
                  <Avatar name={machine.assignedOperatorName} size="md" />
                  <div>
                    <div style={{ fontSize: 14, color: 'var(--color-text-primary)' }}>{machine.assignedOperatorName}</div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginTop: 2 }}>{t('role.operator')} · {machine.siteName}</div>
                  </div>
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Sharing */}
        {activeTab === 'sharing' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 400 }}>{t('machines.ownership_sharing')}</div>
              <Button size="sm" variant="secondary" icon={<IconShare size={13} stroke={1.5} />}>
                {t('machines.share_machine')}
              </Button>
            </div>
            <Card>
              <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 10, fontWeight: 400 }}>{t('machines.current_owner')}</div>
              <div style={{ fontSize: 13, color: 'var(--color-text-primary)', marginBottom: 16 }}>{machine.ownedBy === orgId ? t('machines.your_organisation') : machine.ownedBy}</div>
              <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 10, fontWeight: 400 }}>{t('machines.shared_with')}</div>
              {!machine.sharedWith?.length ? (
                <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>{t('machines.not_shared')}</div>
              ) : machine.sharedWith.map((orgId, i) => (
                <div key={i} style={{ fontSize: 13, color: 'var(--color-text-primary)', padding: '6px 0', borderBottom: '0.5px solid var(--color-border-light)' }}>{orgId}</div>
              ))}
            </Card>

            <div style={{ marginTop: 24 }}>
              <MachinePassportPanel machineId={machineId} machine={machine} highlight={passportHighlight} />
            </div>
          </div>
        )}

        {/* Incidents */}
        {activeTab === 'incidents' && (
          <div>
            <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 16 }}>{t('machines.incident_history')}</div>
            {incidents.length === 0 ? (
              <Card>
                <EmptyState
                  icon={<IconAlertTriangle size={28} stroke={1.5} />}
                  title={t('incidents.none')}
                  message={t('incidents.none_msg')}
                />
              </Card>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {incidents.map(inc => (
                  <Card
                    key={inc.id}
                    onClick={() => navigate(`/incidents/${inc.id}`)}
                    hover
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <div style={{ fontSize: 13, color: 'var(--color-text-primary)', marginBottom: 4 }}>
                          {inc.triggerType === 'impact' ? t('incidents.impact_detected') : inc.triggerType === 'manual' ? t('incidents.manual_sos') : t('incidents.man_down')}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
                          {inc.triggeredByName} · {formatDateTime(inc.detectedAt)}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <StatusPill status={inc.status || 'open'} />
                        <IconArrowRight size={14} color="var(--color-text-tertiary)" stroke={1.5} />
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        </div>
      </div>

      {/* Unblock modal */}
      <Modal
        open={showUnblockModal}
        onClose={() => setShowUnblockModal(false)}
        title={t('machines.unblock_title')}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowUnblockModal(false)}>{t('common.cancel')}</Button>
            <Button
              variant="success"
              onClick={handleUnblock}
              disabled={unblocking || !unblockReason.trim()}
              loading={unblocking}
              icon={<IconLockOpen size={14} stroke={1.5} />}
            >
              {t('machines.confirm_unblock')}
            </Button>
          </>
        }
      >
        <div style={{ background: 'var(--color-danger-light)', border: '0.5px solid var(--color-danger)', borderRadius: 'var(--radius-md)', padding: '10px 14px', marginBottom: 16, fontSize: 13, color: 'var(--color-danger)' }}>
          {t('machines.unblock_warning')}
        </div>
        <div style={{ marginBottom: 8, fontSize: 13, color: 'var(--color-text-secondary)' }}>
          {t('machines.blocked_reason')}: <strong style={{ color: 'var(--color-text-primary)' }}>{machine.blockedReason}</strong>
        </div>
        <div style={{ marginBottom: 8 }}>
          <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
            {t('machines.unblock_statement')} <span style={{ color: 'var(--color-danger)' }}>*</span>
          </label>
          <textarea
            placeholder={t('machines.unblock_placeholder')}
            value={unblockReason}
            onChange={e => setUnblockReason(e.target.value)}
            rows={3}
            style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
          />
        </div>
      </Modal>

    </Layout>
  );
}